"use client";

/**
 * home-deal.tsx — 首屏「一手真牌局」客户端宿主
 *
 * 服务端(server component)已选好一手牌(beginner 优先,按日轮换);
 * 本组件挂载后一次性读 localStorage 的 FSRS due 概念,若有到期复习
 * 且用户尚未与牌桌交互(hover/点过),静默换成一手 due 场景——仅挂载
 * 初期一次,之后绝不替换(不打断正在发生的决策)。
 *
 * 点任一行动按钮 = 入场:/train?scene=<id> 把该场景放队首。
 */

import { useEffect, useRef, useState } from "react";
import Link from "next/link";
import { useRouter } from "next/navigation";
import { motion, useReducedMotion } from "motion/react";
import { ArrowRight, RotateCcw, Shuffle, Spade } from "lucide-react";
import type { PilotScene } from "@/lib/scene-types";
import { buildConceptKey, getDueConcepts } from "@/lib/fsrs";
import { SpotPrompt } from "@/components/spot-prompt";
import { DojoFrame } from "@/components/dojo";
import { DISPLAY_FONT } from "@/components/dojo-theme";
import { cn } from "@/lib/cn";

/** 与 /train 复习封顶同口径 */
const REVIEW_CAP = 20;

export function HomeDeal({
  featured,
  scenes,
}: {
  featured: PilotScene;
  scenes: PilotScene[];
}) {
  const router = useRouter();
  const reduce = useReducedMotion();
  const [scene, setScene] = useState(featured);
  const [dueCount, setDueCount] = useState(0);
  // 一旦用户与牌桌有任何交互,due 补选永不生效(防换牌打断决策)
  const interacted = useRef(false);
  const didRun = useRef(false);

  const markInteracted = () => {
    interacted.current = true;
  };

  // due 补选:仅挂载初期一次性。服务端首帧是 beginner 场景,
  // 这里在首帧之后静默替换,SpotPrompt 自带入场动画承接,无闪屏。
  // (异步 IIFE 延迟 setState,照抄 /train 的 fetch-effect 范式)
  useEffect(() => {
    if (didRun.current) return;
    didRun.current = true;
    (async () => {
      const dueKeys = new Set(getDueConcepts(50).map((c) => c.key));
      if (dueKeys.size === 0) return;
      setDueCount(scenes.filter((s) => dueKeys.has(buildConceptKey(s))).length);
      if (interacted.current) return;
      const next = scenes.find(
        (s) => dueKeys.has(buildConceptKey(s)) && s.scene_id !== featured.scene_id,
      );
      if (next) setScene(next);
    })();
  }, [scenes, featured]);

  const dealAnother = () => {
    markInteracted();
    const others = scenes.filter((s) => s.scene_id !== scene.scene_id);
    if (others.length === 0) return;
    setScene(others[Math.floor(Math.random() * others.length)]);
  };

  const capped = Math.min(dueCount, REVIEW_CAP);

  return (
    <DojoFrame>
      {/* 矮视口(≤860px 高)段间距 36→20:800px 高首屏装下行动按钮(r4 契约 item 17) */}
      <div className="mx-auto flex w-full max-w-3xl flex-col gap-9 py-2 md:min-h-[calc(100dvh-9rem)] md:justify-center md:py-0 [@media(max-height:940px)]:gap-5">
        {/* 道场品牌 + 一句话定位;右侧暖金 chip = 今日待复习(有 due 才出现) */}
        <div>
          <header className="flex flex-wrap items-center justify-between gap-x-6 gap-y-4">
            <div className="flex items-center gap-3">
              <span
                aria-hidden
                className="flex size-9 items-center justify-center rounded-xl bg-[radial-gradient(circle_at_35%_28%,#F7DFA6,#E3B55B_58%,#8A6420)] shadow-[0_0_28px_-6px_rgba(227,181,91,0.6)] ring-1 ring-amber-200/40"
              >
                <Spade className="size-4 text-[#2A1D04]" fill="currentColor" strokeWidth={0} />
              </span>
              <div>
                <p
                  className={cn(
                    DISPLAY_FONT,
                    "text-xl font-extrabold leading-tight tracking-tight text-amber-200",
                  )}
                >
                  GTO 道场
                </p>
                <p className="mt-0.5 text-xs text-stone-500">
                  看局面 · 做决策 · 马上看讲解
                </p>
              </div>
            </div>
            {dueCount > 0 && (
              <Link
                href="/train?mode=review"
                onClick={markInteracted}
                className="inline-flex items-center gap-1.5 rounded-full border border-amber-300/30 bg-amber-300/10 px-3 py-1.5 text-xs font-semibold text-amber-200 transition-colors duration-150 hover:bg-amber-300/[0.16] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-amber-300/60"
              >
                <RotateCcw className="size-3.5" />
                今日 {capped}
                {dueCount > REVIEW_CAP ? "+" : ""} 手待复习
              </Link>
            )}
          </header>
        </div>

        {/* 一手真牌局:牌桌 + 决策点 + 行动按钮,落地即可点 */}
        <div
          className="relative"
          onPointerEnter={markInteracted}
          onPointerDown={markInteracted}
        >
          {/* 灯下呼吸:头顶暖光极缓地明暗,牌桌像真的坐在灯下 */}
          <div
            aria-hidden
            className="pointer-events-none absolute -inset-x-20 -top-14 bottom-0 overflow-visible"
          >
            <div className="absolute left-1/2 top-1/3 -translate-x-1/2 -translate-y-1/2">
              {reduce ? (
                <div className="size-[34rem] rounded-full bg-[radial-gradient(closest-side,rgba(228,184,91,0.09),transparent_72%)]" />
              ) : (
                <motion.div
                  className="size-[34rem] rounded-full bg-[radial-gradient(closest-side,rgba(228,184,91,0.09),transparent_72%)]"
                  animate={{ opacity: [0.55, 1, 0.55], scale: [1, 1.07, 1] }}
                  transition={{ duration: 7, repeat: Infinity, ease: "easeInOut" }}
                />
              )}
            </div>
          </div>
          <div className="relative">
            <SpotPrompt
              scene={scene}
              chosen={null}
              onChoose={() => router.push(`/train?scene=${scene.scene_id}`)}
            />
          </div>
        </div>

        {/* 牌桌脚下:轻出口,不与行动按钮抢注意力 */}
        <div className="flex flex-wrap items-center justify-between gap-3">
          <button
            type="button"
            onClick={dealAnother}
            className="inline-flex items-center gap-1.5 rounded-md text-xs text-stone-500 transition-colors duration-150 hover:text-stone-300 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-emerald-300/70"
          >
            <Shuffle className="size-3.5" />
            换一手牌
          </button>
          <Link
            href="/train"
            onClick={markInteracted}
            className="group inline-flex items-center gap-1 text-xs text-stone-500 transition-colors duration-150 hover:text-emerald-200 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-emerald-300/70"
          >
            想系统地练,去训练场
            <ArrowRight className="size-3 transition-transform duration-200 group-hover:translate-x-0.5" />
          </Link>
        </div>
      </div>
    </DojoFrame>
  );
}

export function HomeEmpty() {
  return (
    <DojoFrame>
      <div className="flex min-h-[70vh] flex-col items-center justify-center gap-5 text-center">
        <Spade className="size-8 text-amber-300/50" />
        <p className="text-sm text-stone-400">牌桌还没摆好,先去看看教材。</p>
        <div className="flex gap-3">
          <Link
            href="/lessons"
            className="rounded-xl border border-[#2A3D30] px-4 py-2 text-sm text-stone-300 transition hover:border-emerald-400/40 hover:text-emerald-200 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-emerald-300/70"
          >
            课库
          </Link>
          <Link
            href="/scenes"
            className="rounded-xl border border-[#2A3D30] px-4 py-2 text-sm text-stone-300 transition hover:border-emerald-400/40 hover:text-emerald-200 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-emerald-300/70"
          >
            场景库
          </Link>
        </div>
      </div>
    </DojoFrame>
  );
}
